import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react'
import {
  Room,
  RoomEvent,
  RemoteParticipant,
  LocalParticipant,
  Track,
  RemoteTrackPublication,
  LocalTrackPublication,
  ConnectionState,
  Participant,
  AudioTrack,
  VideoTrack,
  LocalVideoTrack,
  createLocalScreenTracks,
  createLocalAudioTrack,
  createLocalVideoTrack,
  VideoPresets,
  AudioPresets,
} from 'livekit-client'
import { BackgroundProcessor } from '@livekit/track-processors'

export interface ParticipantInfo {
  identity: string
  name: string
  isSpeaking: boolean
  audioLevel: number
  isMuted: boolean
  isCameraOff: boolean
  isScreenSharing: boolean
  isLocal: boolean
  connectionQuality: string
  audioTrack: AudioTrack | null
  videoTrack: VideoTrack | null
  screenTrack: VideoTrack | null
  screenAudioTrack: AudioTrack | null
}

interface ConnectOptions {
  audioDeviceId?: string | null
  videoDeviceId?: string | null
  highQualityAudio?: boolean
}

export interface AudioQualitySettings {
  highQualityMode: boolean
  noiseSuppression: boolean
  echoCancellation: boolean
  autoGainControl: boolean
  // Custom audio processing settings
  noiseGateEnabled: boolean
  noiseGateThreshold: number // -100 to 0 dB
  noiseGateAttack: number // ms
  noiseGateRelease: number // ms
  gainEnabled: boolean
  gainValue: number // 0 to 2
}

interface LiveKitContextValue {
  // Room state
  room: Room | null
  connectionState: ConnectionState
  isConnected: boolean
  isConnecting: boolean

  // Participants
  participants: ParticipantInfo[]
  localParticipant: ParticipantInfo | null
  remoteParticipants: ParticipantInfo[]

  // Actions
  connect: (url: string, token: string, options?: ConnectOptions) => Promise<void>
  disconnect: () => void
  toggleMute: () => Promise<void>
  toggleCamera: () => Promise<void>
  toggleScreenShare: () => Promise<void>
  toggleBackgroundBlur: () => Promise<void>

  // Device switching
  switchAudioInput: (deviceId: string) => Promise<void>
  switchVideoInput: (deviceId: string) => Promise<void>
  switchAudioOutput: (deviceId: string) => Promise<void>

  // Audio quality
  applyAudioSettings: (settings: AudioQualitySettings) => Promise<void>

  // Background blur
  setBlurStrength: (strength: number) => void

  // Local state
  isMuted: boolean
  isCameraOff: boolean
  isScreenSharing: boolean
  isBackgroundBlurEnabled: boolean
  isBackgroundBlurLoading: boolean
  blurStrength: number
  localAudioTrack: Track | null
  localVideoTrack: Track | null
  localScreenTrack: Track | null

  // Errors
  error: string | null
}

const LiveKitContext = createContext<LiveKitContextValue | null>(null)

function participantToInfo(participant: Participant, isLocal: boolean): ParticipantInfo {
  let audioTrack: AudioTrack | null = null
  let videoTrack: VideoTrack | null = null
  let screenTrack: VideoTrack | null = null
  let screenAudioTrack: AudioTrack | null = null
  let isMuted = true
  let isCameraOff = true
  let isScreenSharing = false

  // Find audio tracks (microphone and screen share audio)
  participant.audioTrackPublications.forEach((pub) => {
    if (pub.track && pub.track.kind === Track.Kind.Audio) {
      if (pub.source === Track.Source.ScreenShareAudio) {
        screenAudioTrack = pub.track as AudioTrack
      } else {
        audioTrack = pub.track as AudioTrack
        isMuted = pub.isMuted
      }
    }
  })

  // Find video tracks (camera and screen share)
  participant.videoTrackPublications.forEach((pub) => {
    if (pub.track && pub.track.kind === Track.Kind.Video) {
      if (pub.source === Track.Source.ScreenShare) {
        screenTrack = pub.track as VideoTrack
        isScreenSharing = true
      } else {
        videoTrack = pub.track as VideoTrack
        isCameraOff = pub.isMuted || !pub.track
      }
    }
  })

  return {
    identity: participant.identity,
    name: participant.name || participant.identity,
    isSpeaking: participant.isSpeaking,
    audioLevel: participant.audioLevel,
    isMuted,
    isCameraOff,
    isScreenSharing,
    isLocal,
    connectionQuality: participant.connectionQuality,
    audioTrack,
    videoTrack,
    screenTrack,
    screenAudioTrack,
  }
}

export function LiveKitProvider({ children }: { children: ReactNode }) {
  const [room, setRoom] = useState<Room | null>(null)
  const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.Disconnected)
  const [participants, setParticipants] = useState<ParticipantInfo[]>([])
  const [localParticipant, setLocalParticipant] = useState<ParticipantInfo | null>(null)
  const [isMuted, setIsMuted] = useState(false)
  const [isCameraOff, setIsCameraOff] = useState(true)
  const [isScreenSharing, setIsScreenSharing] = useState(false)
  const [localAudioTrack, setLocalAudioTrack] = useState<Track | null>(null)
  const [localVideoTrack, setLocalVideoTrack] = useState<Track | null>(null)
  const [localScreenTrack, setLocalScreenTrack] = useState<Track | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Background blur state
  const [isBackgroundBlurEnabled, setIsBackgroundBlurEnabled] = useState(false)
  const [isBackgroundBlurLoading, setIsBackgroundBlurLoading] = useState(false)
  const [blurStrength, setBlurStrengthState] = useState(50) // 0-100, default 50

  const roomRef = useRef<Room | null>(null)
  const backgroundProcessorRef = useRef<ReturnType<typeof BackgroundProcessor> | null>(null)

  // Update participants list
  const updateParticipants = useCallback(() => {
    if (!roomRef.current) return

    const r = roomRef.current
    const allParticipants: ParticipantInfo[] = []

    // Add local participant
    if (r.localParticipant) {
      const localInfo = participantToInfo(r.localParticipant, true)
      setLocalParticipant(localInfo)
      allParticipants.push(localInfo)

      // Update local state
      r.localParticipant.audioTrackPublications.forEach((pub) => {
        if (pub.track) {
          setIsMuted(pub.isMuted)
          setLocalAudioTrack(pub.track)
        }
      })

      r.localParticipant.videoTrackPublications.forEach((pub) => {
        if (pub.track) {
          if (pub.source === Track.Source.ScreenShare) {
            setIsScreenSharing(!pub.isMuted)
            setLocalScreenTrack(pub.track)
          } else {
            setIsCameraOff(pub.isMuted)
            setLocalVideoTrack(pub.track)
          }
        }
      })
    }

    // Add remote participants
    r.remoteParticipants.forEach((participant) => {
      allParticipants.push(participantToInfo(participant, false))
    })

    setParticipants(allParticipants)
  }, [])

  const connect = useCallback(async (url: string, token: string, options?: ConnectOptions) => {
    setError(null)

    // Determine audio settings based on quality mode
    const useHighQuality = options?.highQualityAudio ?? false

    try {
      // Create new room with device preferences
      const newRoom = new Room({
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: {
          resolution: VideoPresets.h720.resolution,
          // Use specific device if provided (important for capture cards)
          ...(options?.videoDeviceId ? { deviceId: options.videoDeviceId } : {}),
        },
        audioCaptureDefaults: {
          echoCancellation: true,
          // High quality mode disables noise processing for cleaner audio
          noiseSuppression: !useHighQuality,
          autoGainControl: !useHighQuality,
          // Use specific device if provided
          ...(options?.audioDeviceId ? { deviceId: options.audioDeviceId } : {}),
        },
        // Use music preset for high quality audio
        publishDefaults: useHighQuality ? {
          audioPreset: AudioPresets.music,
          dtx: false, // Disable discontinuous transmission for better quality
        } : undefined,
      })

      roomRef.current = newRoom
      setRoom(newRoom)

      // Set up event listeners
      newRoom.on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
        setConnectionState(state)
        if (state === ConnectionState.Connected) {
          updateParticipants()
        }
      })

      newRoom.on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
        console.log('Participant connected:', participant.identity)
        updateParticipants()
      })

      newRoom.on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
        console.log('Participant disconnected:', participant.identity)
        updateParticipants()
      })

      newRoom.on(RoomEvent.TrackSubscribed, (
        track: Track,
        publication: RemoteTrackPublication,
        participant: RemoteParticipant
      ) => {
        console.log('Track subscribed:', track.kind, participant.identity)
        updateParticipants()
      })

      newRoom.on(RoomEvent.TrackUnsubscribed, (
        track: Track,
        publication: RemoteTrackPublication,
        participant: RemoteParticipant
      ) => {
        console.log('Track unsubscribed:', track.kind, participant.identity)
        updateParticipants()
      })

      newRoom.on(RoomEvent.LocalTrackPublished, (
        publication: LocalTrackPublication,
        participant: LocalParticipant
      ) => {
        console.log('Local track published:', publication.kind)
        updateParticipants()
      })

      newRoom.on(RoomEvent.LocalTrackUnpublished, (
        publication: LocalTrackPublication,
        participant: LocalParticipant
      ) => {
        console.log('Local track unpublished:', publication.kind)
        updateParticipants()
      })

      newRoom.on(RoomEvent.TrackMuted, () => {
        updateParticipants()
      })

      newRoom.on(RoomEvent.TrackUnmuted, () => {
        updateParticipants()
      })

      newRoom.on(RoomEvent.ActiveSpeakersChanged, () => {
        updateParticipants()
      })

      newRoom.on(RoomEvent.Disconnected, () => {
        setConnectionState(ConnectionState.Disconnected)
        setParticipants([])
        setLocalParticipant(null)
        setLocalAudioTrack(null)
        setLocalVideoTrack(null)
        setLocalScreenTrack(null)
        setIsScreenSharing(false)
      })

      newRoom.on(RoomEvent.Reconnecting, () => {
        console.log('Reconnecting to room...')
      })

      newRoom.on(RoomEvent.Reconnected, () => {
        console.log('Reconnected to room')
        updateParticipants()
      })

      // Connect to room
      await newRoom.connect(url, token)

      // Enable microphone and camera by default
      await newRoom.localParticipant.setMicrophoneEnabled(true)
      await newRoom.localParticipant.setCameraEnabled(true)

      setConnectionState(ConnectionState.Connected)
      setIsMuted(false)
      setIsCameraOff(false)
      updateParticipants()

    } catch (err) {
      console.error('Failed to connect to LiveKit room:', err)
      setError(err instanceof Error ? err.message : 'Failed to connect to room')
      setConnectionState(ConnectionState.Disconnected)
    }
  }, [updateParticipants])

  const disconnect = useCallback(() => {
    if (roomRef.current) {
      roomRef.current.disconnect()
      roomRef.current = null
      setRoom(null)
      setConnectionState(ConnectionState.Disconnected)
      setParticipants([])
      setLocalParticipant(null)
      setLocalAudioTrack(null)
      setLocalVideoTrack(null)
      setLocalScreenTrack(null)
      setIsMuted(false)
      setIsCameraOff(true)
      setIsScreenSharing(false)
    }
  }, [])

  const toggleMute = useCallback(async () => {
    if (!roomRef.current?.localParticipant) return

    const newMuteState = !isMuted
    await roomRef.current.localParticipant.setMicrophoneEnabled(!newMuteState)
    setIsMuted(newMuteState)
    updateParticipants()
  }, [isMuted, updateParticipants])

  const toggleCamera = useCallback(async () => {
    if (!roomRef.current?.localParticipant) return

    try {
      if (isCameraOff) {
        // Camera is currently off, turning it on
        // Get the current video publication to find the device ID
        const currentVideoPub = Array.from(roomRef.current.localParticipant.videoTrackPublications.values())
          .find(pub => pub.source === Track.Source.Camera)

        // Get the device ID from the current track if it exists
        let deviceId: string | undefined
        if (currentVideoPub?.track) {
          const videoTrack = currentVideoPub.track as VideoTrack
          deviceId = videoTrack.mediaStreamTrack?.getSettings()?.deviceId
        }

        // First try the standard method
        await roomRef.current.localParticipant.setCameraEnabled(true)

        // Check if the track is actually producing video after a short delay
        setTimeout(async () => {
          if (!roomRef.current?.localParticipant) return

          const newVideoPub = Array.from(roomRef.current.localParticipant.videoTrackPublications.values())
            .find(pub => pub.source === Track.Source.Camera)

          if (newVideoPub?.track) {
            const track = newVideoPub.track as LocalVideoTrack
            const mediaTrack = track.mediaStreamTrack

            // If the track is not live or has no readyState, recreate it
            if (!mediaTrack || mediaTrack.readyState !== 'live') {
              console.log('Camera track not live, recreating...')

              // Unpublish the broken track
              await roomRef.current.localParticipant.unpublishTrack(track)

              // Create a fresh video track
              const newTrack = await createLocalVideoTrack({
                resolution: VideoPresets.h720.resolution,
                deviceId: deviceId,
              })

              // Publish the new track
              await roomRef.current.localParticipant.publishTrack(newTrack, {
                source: Track.Source.Camera,
              })

              console.log('Camera track recreated successfully')
              updateParticipants()
            }
          }
        }, 500)

        setIsCameraOff(false)
      } else {
        // Camera is currently on, turning it off
        await roomRef.current.localParticipant.setCameraEnabled(false)
        setIsCameraOff(true)
      }

      updateParticipants()
    } catch (err) {
      console.error('Failed to toggle camera:', err)
      setError('Failed to toggle camera')
    }
  }, [isCameraOff, updateParticipants])

  const toggleScreenShare = useCallback(async () => {
    if (!roomRef.current?.localParticipant) return

    try {
      if (isScreenSharing) {
        // Stop screen sharing
        const publications = roomRef.current.localParticipant.videoTrackPublications
        publications.forEach(async (pub) => {
          if (pub.source === Track.Source.ScreenShare && pub.track) {
            await roomRef.current?.localParticipant.unpublishTrack(pub.track)
          }
        })
        setIsScreenSharing(false)
        setLocalScreenTrack(null)
      } else {
        // Start screen sharing with high quality settings for readability
        const tracks = await createLocalScreenTracks({
          audio: true, // Enable screen share audio (e.g., for sharing videos, music)
          video: true,
          // Resolution is controlled by browser's screen picker
          // Quality is controlled via publish options below
        })

        for (const track of tracks) {
          if (track.kind === Track.Kind.Video) {
            // Publish screen share with high bitrate for text clarity
            await roomRef.current.localParticipant.publishTrack(track, {
              source: Track.Source.ScreenShare,
              videoEncoding: {
                maxBitrate: 3_000_000, // 3 Mbps for crisp text
                maxFramerate: 15,
              },
              screenShareEncoding: {
                maxBitrate: 3_000_000,
                maxFramerate: 15,
              },
            })
            setLocalScreenTrack(track)
          } else {
            // Audio track
            await roomRef.current.localParticipant.publishTrack(track, {
              source: Track.Source.ScreenShareAudio,
            })
          }
        }
        setIsScreenSharing(true)
      }
      updateParticipants()
    } catch (err) {
      console.error('Failed to toggle screen share:', err)
      // User cancelled screen share dialog
      if (err instanceof Error && err.name !== 'NotAllowedError') {
        setError('Failed to share screen')
      }
    }
  }, [isScreenSharing, updateParticipants])

  // Switch audio input device (microphone)
  const switchAudioInput = useCallback(async (deviceId: string) => {
    if (!roomRef.current) return
    try {
      await roomRef.current.switchActiveDevice('audioinput', deviceId)
      console.log('Switched audio input to:', deviceId)
      updateParticipants()
    } catch (err) {
      console.error('Failed to switch audio input:', err)
      setError('Failed to switch microphone')
    }
  }, [updateParticipants])

  // Switch video input device (camera)
  const switchVideoInput = useCallback(async (deviceId: string) => {
    if (!roomRef.current) return
    try {
      await roomRef.current.switchActiveDevice('videoinput', deviceId)
      console.log('Switched video input to:', deviceId)
      updateParticipants()
    } catch (err) {
      console.error('Failed to switch video input:', err)
      setError('Failed to switch camera')
    }
  }, [updateParticipants])

  // Switch audio output device (speaker)
  const switchAudioOutput = useCallback(async (deviceId: string) => {
    if (!roomRef.current) return
    try {
      await roomRef.current.switchActiveDevice('audiooutput', deviceId)
      console.log('Switched audio output to:', deviceId)
    } catch (err) {
      console.error('Failed to switch audio output:', err)
      setError('Failed to switch speaker')
    }
  }, [])

  // Toggle background blur
  const toggleBackgroundBlur = useCallback(async () => {
    if (!roomRef.current?.localParticipant) return

    try {
      // Get current camera track
      const currentVideoPub = Array.from(roomRef.current.localParticipant.videoTrackPublications.values())
        .find(pub => pub.source === Track.Source.Camera)

      if (!currentVideoPub?.track) {
        setError('No camera track to apply blur to')
        return
      }

      const videoTrack = currentVideoPub.track as LocalVideoTrack

      if (isBackgroundBlurEnabled) {
        // Disable blur
        console.log('🎥 Disabling background blur...')

        // Stop the processor
        await videoTrack.stopProcessor()
        backgroundProcessorRef.current = null

        setIsBackgroundBlurEnabled(false)
        console.log('🎥 Background blur disabled')
      } else {
        // Enable blur
        console.log('🎥 Enabling background blur...')
        setIsBackgroundBlurLoading(true)

        // Convert blur strength (0-100) to radius (5-20 pixels)
        const blurRadius = 5 + (blurStrength / 100) * 15

        // Create and apply the background processor
        const processor = BackgroundProcessor({ mode: 'background-blur', blurRadius })
        backgroundProcessorRef.current = processor

        await videoTrack.setProcessor(processor)

        setIsBackgroundBlurEnabled(true)
        setIsBackgroundBlurLoading(false)
        console.log('🎥 Background blur enabled with radius:', blurRadius)
      }

      updateParticipants()
    } catch (err) {
      console.error('Failed to toggle background blur:', err)
      setError('Failed to toggle background blur')
      setIsBackgroundBlurLoading(false)
    }
  }, [isBackgroundBlurEnabled, blurStrength, updateParticipants])

  // Set blur strength
  const setBlurStrength = useCallback((strength: number) => {
    setBlurStrengthState(strength)
    if (backgroundProcessorRef.current && isBackgroundBlurEnabled) {
      // Convert blur strength (0-100) to radius (5-20 pixels)
      const blurRadius = 5 + (strength / 100) * 15
      backgroundProcessorRef.current.switchTo({ mode: 'background-blur', blurRadius })
    }
  }, [isBackgroundBlurEnabled])

  // Apply audio quality settings (restarts mic with new settings)
  // NOTE: Custom audio processing (noise gate/gain) is temporarily disabled
  // until we fix the track publishing issue. Only browser-level settings work for now.
  const applyAudioSettings = useCallback(async (settings: AudioQualitySettings) => {
    if (!roomRef.current?.localParticipant) return

    try {
      // Get current mic track
      const currentMicPub = Array.from(roomRef.current.localParticipant.audioTrackPublications.values())
        .find(pub => pub.source === Track.Source.Microphone)

      if (!currentMicPub?.track) return

      // Get current device ID
      const currentTrack = currentMicPub.track as AudioTrack
      const currentDeviceId = currentTrack.mediaStreamTrack?.getSettings()?.deviceId

      // Unpublish current track
      await roomRef.current.localParticipant.unpublishTrack(currentMicPub.track)

      // Create new track with updated settings
      const newTrack = await createLocalAudioTrack({
        echoCancellation: settings.echoCancellation,
        noiseSuppression: settings.highQualityMode ? false : settings.noiseSuppression,
        autoGainControl: settings.highQualityMode ? false : settings.autoGainControl,
        deviceId: currentDeviceId,
      })

      // Publish new track
      await roomRef.current.localParticipant.publishTrack(newTrack, {
        audioPreset: settings.highQualityMode ? AudioPresets.music : undefined,
        dtx: settings.highQualityMode ? false : true,
      })

      console.log('Applied audio settings:', settings)
      updateParticipants()
    } catch (err) {
      console.error('Failed to apply audio settings:', err)
      setError('Failed to apply audio settings')
    }
  }, [updateParticipants])

  // Cleanup on unmount (only when provider unmounts, not tab changes)
  useEffect(() => {
    return () => {
      if (roomRef.current) {
        roomRef.current.disconnect()
      }
      // Clean up background processor
      backgroundProcessorRef.current = null
    }
  }, [])

  const remoteParticipants = participants.filter(p => !p.isLocal)
  const isConnecting = connectionState === ConnectionState.Connecting

  const value: LiveKitContextValue = {
    room,
    connectionState,
    isConnected: connectionState === ConnectionState.Connected,
    isConnecting,
    participants,
    localParticipant,
    remoteParticipants,
    connect,
    disconnect,
    toggleMute,
    toggleCamera,
    toggleScreenShare,
    toggleBackgroundBlur,
    switchAudioInput,
    switchVideoInput,
    switchAudioOutput,
    applyAudioSettings,
    setBlurStrength,
    isMuted,
    isCameraOff,
    isScreenSharing,
    isBackgroundBlurEnabled,
    isBackgroundBlurLoading,
    blurStrength,
    localAudioTrack,
    localVideoTrack,
    localScreenTrack,
    error,
  }

  return (
    <LiveKitContext.Provider value={value}>
      {children}
    </LiveKitContext.Provider>
  )
}

export function useLiveKit(): LiveKitContextValue {
  const context = useContext(LiveKitContext)
  if (!context) {
    throw new Error('useLiveKit must be used within a LiveKitProvider')
  }
  return context
}
